/**
 * Opening up one family on the invitation list.
 *
 * A spreadsheet is where most of this comes from, but not all of it: a sibling
 * nobody mentioned, a cousin remembered late, a name spelled wrong. Every one
 * of those is fixable here without going back to Excel and re-importing.
 */

import { useState } from 'react';
import { host, type HouseholdRow, type Person } from '../api.js';
import { copyText } from './copy.js';

const ROLE_LABEL: Record<string, string> = {
  anchor_child: 'criança convidada',
  mother: 'mãe',
  father: 'pai',
  sibling: 'irmão ou irmã',
  guardian: 'responsável',
  nanny: 'babá',
  other: 'outro',
};

/** Roles that are children, who are brought rather than doing the bringing. */
const CHILD_ROLES = new Set(['anchor_child', 'sibling']);

export function HouseholdEditor({
  eventId,
  row,
  onChanged,
  onRemoved,
}: {
  eventId: string;
  row: HouseholdRow;
  onChanged: (row: HouseholdRow) => void;
  onRemoved: () => void;
}) {
  const [label, setLabel] = useState(row.label);
  const [source, setSource] = useState(row.source ?? '');
  const [contactName, setContactName] = useState(row.contactName ?? '');
  const [people, setPeople] = useState<Person[]>(row.people);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [copied, setCopied] = useState(false);
  const [guestMessage, setGuestMessage] = useState(row.guestMessage ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function saveHousehold() {
    setBusy(true);
    setError(null);
    try {
      await host.editHousehold(eventId, row.householdId, {
        label,
        source,
        contactName,
        guestMessage,
      });
      onChanged({ ...row, label, source, contactName, guestMessage, people });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function addPerson(name: string, role: string) {
    const { person } = await host.addPerson(eventId, row.householdId, {
      name,
      role,
    });
    const next = [...people, person];
    setPeople(next);
    onChanged({ ...row, people: next });
  }

  /**
   * The host answering for somebody.
   *
   * Most families tell her at the school gate rather than tapping a link, and
   * without this there is nowhere to write that down.
   */
  async function answerFor(personId: string, rsvpState: string) {
    const { person } = await host.editPerson(eventId, row.householdId, personId, {
      rsvpState,
    });
    const next = people.map((p) => (p.personId === personId ? person : p));
    setPeople(next);
    onChanged({ ...row, people: next });
  }

  async function editPerson(personId: string, patch: Record<string, unknown>) {
    const { person } = await host.editPerson(
      eventId,
      row.householdId,
      personId,
      patch,
    );
    const next = people.map((p) => (p.personId === personId ? person : p));
    setPeople(next);
    onChanged({ ...row, people: next });
  }

  async function removePerson(personId: string) {
    await host.removePerson(eventId, row.householdId, personId);
    const next = people.filter((p) => p.personId !== personId);
    setPeople(next);
    onChanged({ ...row, people: next });
  }

  const dirty =
    label !== row.label ||
    source !== (row.source ?? '') ||
    contactName !== (row.contactName ?? '') ||
    guestMessage !== (row.guestMessage ?? '');

  return (
    <div className="editor">
      {error && <div className="banner error">{error}</div>}

      <div className="row-2" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div>
          <label>Convite para</label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>
        <div>
          <label>De onde</label>
          <input
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder="Escola"
          />
        </div>
      </div>

      <label>Responsável pela família</label>
      <input
        value={contactName}
        onChange={(e) => setContactName(e.target.value)}
        placeholder="Mãe ou pai"
      />

      <label>Recado só para esta família (opcional)</label>
      <textarea
        rows={2}
        maxLength={300}
        value={guestMessage}
        onChange={(e) => setGuestMessage(e.target.value)}
        placeholder="Ex.: a Bia pode vir também!"
      />

      {dirty && (
        <button
          className="primary"
          style={{ marginTop: '0.6rem' }}
          onClick={saveHousehold}
          disabled={busy || label.trim() === ''}
        >
          {busy ? 'Salvando…' : 'Salvar'}
        </button>
      )}

      <label>Link desta família</label>
      <div className="row-2" style={{ gridTemplateColumns: '1fr auto' }}>
        <input readOnly value={row.invitationUrl} onFocus={(e) => e.target.select()} />
        <button
          style={{ width: 'auto' }}
          onClick={async () => {
            const ok = await copyText(row.invitationUrl);
            setCopied(ok);
            setTimeout(() => setCopied(false), 2000);
          }}
        >
          {copied ? 'Copiado' : 'Copiar'}
        </button>
      </div>

      <div className="hint-inline">
        <span>{row.sentAt ? 'Convite marcado como enviado' : 'Convite não enviado'}</span>
        <button
          className="linkish"
          onClick={async () => {
            const sent = !row.sentAt;
            await host.editHousehold(eventId, row.householdId, { sent });
            onChanged({
              ...row,
              sentAt: sent ? new Date().toISOString() : null,
              people,
            });
          }}
        >
          {row.sentAt ? 'marcar como não enviado' : 'marcar como enviado'}
        </button>
      </div>

      <h3 className="editor-heading">Pessoas ({people.length})</h3>
      <div className="card" style={{ marginBottom: '0.5rem' }}>
        {people.length === 0 && (
          <p className="muted" style={{ margin: 0 }}>
            Ninguém nesta família ainda.
          </p>
        )}
        {people.map((person) => (
          <PersonEditor
            key={person.personId}
            person={person}
            onSave={(patch) => editPerson(person.personId, patch)}
            onRemove={() => removePerson(person.personId)}
            onAnswer={(state) => answerFor(person.personId, state)}
          />
        ))}
      </div>

      <AddPerson onAdd={addPerson} />

      {confirmRemove ? (
        <div className="card" style={{ marginTop: '0.75rem' }}>
          <div className="banner error">
            Tirar {row.label} da lista. O link que você já mandou para essa
            família para de funcionar.
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className="danger"
              onClick={async () => {
                setBusy(true);
                try {
                  await host.removeHousehold(eventId, row.householdId);
                  onRemoved();
                } catch (err) {
                  setError((err as Error).message);
                  setBusy(false);
                }
              }}
              disabled={busy}
            >
              {busy ? 'Tirando…' : 'Tirar da lista'}
            </button>
            <button onClick={() => setConfirmRemove(false)}>Cancelar</button>
          </div>
        </div>
      ) : (
        <button
          className="danger-outline"
          style={{ marginTop: '0.75rem' }}
          onClick={() => setConfirmRemove(true)}
        >
          Tirar esta família da lista
        </button>
      )}
    </div>
  );
}

function PersonEditor({
  person,
  onSave,
  onRemove,
  onAnswer,
}: {
  person: Person;
  onSave: (patch: Record<string, unknown>) => Promise<void>;
  onRemove: () => Promise<void>;
  onAnswer: (state: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(person.name);
  const [role, setRole] = useState(person.role);
  const [age, setAge] = useState(person.ageYears?.toString() ?? '');
  const [busy, setBusy] = useState(false);
  const isChild = CHILD_ROLES.has(person.role);

  if (!open) {
    return (
      <div className="person">
        <div className="row" style={{ borderBottom: 0, paddingBottom: 0 }}>
          <div className="grow">
            <div className="label-main">{person.name}</div>
            <div className="muted">
              {ROLE_LABEL[person.role] ?? person.role}
              {person.ageYears !== null && `, ${person.ageYears} anos`}
            </div>
          </div>
          <button onClick={() => setOpen(true)}>Editar</button>
        </div>

        {/* Answering here, because a family who told her in person still has
            to end up in the count. Tapping the current answer takes it back.
            Children get two answers: they do not drop themselves off. */}
        <div className={isChild ? 'choices two' : 'choices'}>
          {(isChild
            ? ([
                ['attending_staying', 'stay', 'Vem'],
                ['not_attending', 'no', 'Não vem'],
              ] as const)
            : ([
                ['attending_staying', 'stay', 'Vem e fica'],
                ['attending_dropoff', 'drop', 'Deixa e volta'],
                ['not_attending', 'no', 'Não vem'],
              ] as const)
          ).map(([state, tone, text]) => (
            <button
              key={state}
              type="button"
              className={`choice ${tone}`}
              aria-pressed={person.rsvpState === state}
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await onAnswer(person.rsvpState === state ? 'pending' : state);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {text}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="person">
      <label>Nome</label>
      <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />

      <div className="row-2" style={{ gridTemplateColumns: '1fr 6rem' }}>
        <div>
          <label>Quem é</label>
          <select value={role} onChange={(e) => setRole(e.target.value)}>
            {Object.entries(ROLE_LABEL).map(([value, text]) => (
              <option key={value} value={value}>
                {text}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Idade</label>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            max={120}
            value={age}
            onChange={(e) => setAge(e.target.value)}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem' }}>
        <button
          className="primary"
          disabled={busy || name.trim() === ''}
          onClick={async () => {
            setBusy(true);
            try {
              await onSave({
                name,
                role,
                ageYears: age === '' ? null : Number(age),
              });
              setOpen(false);
            } finally {
              setBusy(false);
            }
          }}
        >
          Salvar
        </button>
        <button onClick={() => setOpen(false)}>Cancelar</button>
        <button
          className="danger-outline"
          style={{ width: 'auto' }}
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            await onRemove();
          }}
        >
          Tirar
        </button>
      </div>
    </div>
  );
}

function AddPerson({
  onAdd,
}: {
  onAdd: (name: string, role: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [role, setRole] = useState('sibling');
  const [busy, setBusy] = useState(false);

  if (!open) {
    return (
      <button className="linkish" onClick={() => setOpen(true)}>
        + Adicionar pessoa
      </button>
    );
  }

  return (
    <div className="card">
      <label>Nome</label>
      <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />

      <label>Quem é</label>
      <select value={role} onChange={(e) => setRole(e.target.value)}>
        {Object.entries(ROLE_LABEL).map(([value, text]) => (
          <option key={value} value={value}>
            {text}
          </option>
        ))}
      </select>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem' }}>
        <button
          className="primary"
          disabled={busy || name.trim() === ''}
          onClick={async () => {
            setBusy(true);
            try {
              await onAdd(name, role);
              setName('');
              setOpen(false);
            } finally {
              setBusy(false);
            }
          }}
        >
          Adicionar
        </button>
        <button onClick={() => setOpen(false)}>Cancelar</button>
      </div>
    </div>
  );
}

/** A family that never came from a spreadsheet. */
export function AddHousehold({
  eventId,
  onAdded,
}: {
  eventId: string;
  onAdded: (row: HouseholdRow) => void;
}) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [source, setSource] = useState('');
  const [contactName, setContactName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) {
    return (
      <button
        className="block"
        style={{ marginTop: '1rem' }}
        onClick={() => setOpen(true)}
      >
        + Adicionar família
      </button>
    );
  }

  return (
    <div className="card" style={{ marginTop: '1rem' }}>
      <strong>Nova família</strong>
      {error && <div className="banner error">{error}</div>}

      <label>Convite para</label>
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Ex.: Vovó Ana, ou o nome da criança"
        autoFocus
      />

      <div className="row-2" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div>
          <label>De onde</label>
          <input
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder="Família"
          />
        </div>
        <div>
          <label>Responsável</label>
          <input
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
          />
        </div>
      </div>

      <p className="muted" style={{ margin: '0.6rem 0 0', fontSize: '0.85rem' }}>
        As pessoas você adiciona depois.
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem' }}>
        <button
          className="primary"
          disabled={busy || label.trim() === ''}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              const { household } = await host.addHousehold(eventId, {
                label,
                source,
                contactName,
              });
              onAdded(household);
              setLabel('');
              setSource('');
              setContactName('');
              setOpen(false);
            } catch (err) {
              setError((err as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Criando…' : 'Criar convite'}
        </button>
        <button onClick={() => setOpen(false)}>Cancelar</button>
      </div>
    </div>
  );
}
